export type ApiEnvironment = 'production' | 'staging' | 'local' | 'custom';
export interface ApiEndpoints {
  lawvoApi: string;
  dcApi: string;
}
const ENV_STORAGE_KEY = 'lawvo_api_environment';
const CUSTOM_ENDPOINTS_KEY = 'lawvo_custom_api_endpoints';

// Safely read Vite env vars. In some runtimes (e.g. preview sandboxes)
// `import.meta.env` can be undefined, so dereferencing it directly throws
// "Cannot read properties of undefined" and crashes the app on boot.
function getEnv(key: string): string | undefined {
  try {
    const env = (import.meta as unknown as {
      env?: Record<string, string | undefined>;
    }).env;
    return env ? env[key] : undefined;
  } catch {
    return undefined;
  }
}
const IS_DEV = (() => {
  try {
    const env = (import.meta as unknown as {
      env?: Record<string, unknown>;
    }).env;
    return Boolean(env?.DEV);
  } catch {
    return false;
  }
})();
export const API_ENV_PRESETS: Record<Exclude<ApiEnvironment, 'custom'>, ApiEndpoints> = {
  production: {
    lawvoApi: 'https://api.lawvo.com',
    dcApi: 'https://dcapi.lawvo.com'
  },
  staging: {
    lawvoApi: getEnv('VITE_STAGING_LAWVO_API') || 'https://staging-api.lawvo.com',
    dcApi: getEnv('VITE_STAGING_DC_API') || 'https://staging-dcapi.lawvo.com'
  },
  local: {
    lawvoApi: getEnv('VITE_LOCAL_LAWVO_API') || 'http://localhost:3030',
    dcApi: getEnv('VITE_LOCAL_DC_API') || 'http://localhost:1367'
  }
};
function readStoredEnvironment(): ApiEnvironment {
  try {
    const stored = localStorage.getItem(ENV_STORAGE_KEY);
    if (stored === 'production' || stored === 'staging' || stored === 'local' || stored === 'custom') {
      return stored;
    }
  } catch {

    // ignore
  }
  const viteDefault = getEnv('VITE_API_ENVIRONMENT');
  if (viteDefault === 'production' || viteDefault === 'staging' || viteDefault === 'local' || viteDefault === 'custom') {
    return viteDefault;
  }
  return IS_DEV ? 'local' : 'production';
}
function readCustomEndpoints(): ApiEndpoints | null {
  try {
    const stored = localStorage.getItem(CUSTOM_ENDPOINTS_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (parsed?.lawvoApi && parsed?.dcApi) return parsed;
  } catch {

    // ignore
  }
  return null;
}
export function getApiEnvironment(): ApiEnvironment {
  return readStoredEnvironment();
}
export function getApiEndpoints(): ApiEndpoints {
  const env = readStoredEnvironment();
  if (env === 'custom') {
    return readCustomEndpoints() || {
      lawvoApi: getEnv('VITE_LAWVO_API') || API_ENV_PRESETS.production.lawvoApi,
      dcApi: getEnv('VITE_DC_API') || API_ENV_PRESETS.production.dcApi
    };
  }
  return API_ENV_PRESETS[env];
}
export function setApiEnvironment(env: ApiEnvironment, customEndpoints?: ApiEndpoints): void {
  localStorage.setItem(ENV_STORAGE_KEY, env);
  if (env === 'custom' && customEndpoints) {
    localStorage.setItem(CUSTOM_ENDPOINTS_KEY, JSON.stringify(customEndpoints));
  }
}
export function getLawvoApiBase(): string {
  return getApiEndpoints().lawvoApi.replace(/\/$/, '');
}
export function getDcApiBase(): string {
  return getApiEndpoints().dcApi.replace(/\/$/, '');
}